import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  ArrowRight,
  X,
  Clock,
  Calendar,
  FileText,
  ArrowUpRight,
  Building2
} from 'lucide-react';

const blogPosts = [
  {
    id: 1,
    title: 'Optimización de APIs empresariales con arquitecturas modernas',
    excerpt: 'Cómo reducir la latencia y mejorar la seguridad en las integraciones de sistemas críticos mediante patrones de diseño actualizados.',
    date: '15 Ene, 2025',
    readTime: '5 min',
    image: '/images/sistema/gastro1.png',
    content: `
      <p class="mb-4">En el entorno empresarial actual, la velocidad y seguridad de las APIs no son un lujo, son un requisito. En <strong>Visioner7</strong>, implementamos arquitecturas que priorizan la eficiencia sin sacrificar la integridad de los datos.</p>
      <h3 class="text-xl font-bold text-slate-900 mt-6 mb-3">Impacto en la eficiencia operativa</h3>
      <p class="mb-4">Nuestra metodología reciente en el sector fintech permitió una <strong>reducción del 40% en los tiempos de respuesta</strong> de los endpoints críticos. Esto se logró mediante:</p>
      <ul class="list-disc pl-5 space-y-2 mb-4 text-slate-700">
        <li>Implementación de caché distribuido inteligente.</li>
        <li>Validación de esquemas estricta en el borde (Edge).</li>
        <li>Monitoreo proactivo con alertas automatizadas.</li>
      </ul>
      <p>La modernización de APIs no se trata solo de tecnología, sino de habilitar que tu negocio escale sin fricciones técnicas.</p>
    `,
    views: 1245,
    author: 'María González',
    authorRole: 'Líder de Arquitectura de Software',
    category: 'Arquitectura',
    featured: true
  },
  {
    id: 2,
    title: 'Automatización contable: De la hoja de cálculo al ERP inteligente',
    excerpt: 'Guía práctica para migrar procesos financieros manuales a sistemas centralizados que eliminan errores y ahorran horas de gestión.',
    date: '10 Ene, 2025',
    readTime: '7 min',
    image: '/images/sistema/gastro1.png',
    content: `
      <p class="mb-4">La dependencia de hojas de cálculo para la gestión financiera es uno de los mayores riesgos operativos para las empresas en crecimiento. Los errores manuales y la falta de trazabilidad pueden costar miles de dólares.</p>
      <h3 class="text-xl font-bold text-slate-900 mt-6 mb-3">La transición estratégica</h3>
      <p class="mb-4">Implementar un sistema ERP no debe detener tu operación. En Visioner7, utilizamos un enfoque de migración por fases:</p>
      <ul class="list-disc pl-5 space-y-2 mb-4 text-slate-700">
        <li>Auditoría y mapeo de procesos actuales.</li>
        <li>Configuración de reglas de automatización fiscal.</li>
        <li>Capacitación del equipo financiero antes del despliegue.</li>
      </ul>
      <p>El resultado: conciliaciones bancarias que pasaron de tomar días a ejecutarse en minutos.</p>
    `,
    views: 987,
    author: 'Carlos Rodríguez',
    authorRole: 'Consultor Senior ERP',
    category: 'Finanzas',
    featured: true
  },
  {
    id: 3,
    title: 'Seguridad en aplicaciones móviles corporativas',
    excerpt: 'Estándares y prácticas esenciales para proteger los datos de tu empresa y tus clientes en entornos iOS y Android.',
    date: '05 Ene, 2025',
    readTime: '6 min',
    image: '/images/sistema/gastro1.png',
    content: `<p class="mb-4">El desarrollo móvil empresarial exige un nivel de seguridad superior al de las aplicaciones de consumo. Desde el cifrado de datos en reposo hasta la autenticación multifactor (MFA), cada capa debe ser blindada.</p><p>En este artículo detallamos cómo implementamos estándares OWASP en nuestros desarrollos móviles.</p>`,
    views: 1567,
    author: 'Ana Martínez',
    authorRole: 'Especialista en Ciberseguridad',
    category: 'Seguridad',
    featured: false
  },
  {
    id: 4,
    title: 'Tendencias en desarrollo web B2B para el próximo año',
    excerpt: 'De las Progressive Web Apps (PWA) a la computación en el borde: qué tecnologías están definiendo el estándar de la industria.',
    date: '28 Dic, 2024',
    readTime: '4 min',
    image: '/images/sistema/gastro1.png',
    content: `<p class="mb-4">El desarrollo web B2B está evolucionando hacia experiencias que rivalizan con las aplicaciones nativas en rendimiento, pero con la accesibilidad de la web.</p><p>Analizamos el impacto de Next.js, las arquitecturas serverless y la importancia de los tiempos de carga en la conversión de clientes empresariales.</p>`,
    views: 2034,
    author: 'David Chen',
    authorRole: 'Arquitecto Frontend',
    category: 'Desarrollo Web',
    featured: false
  }
];

const Blog = () => {
  const [selectedPost, setSelectedPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('todos');

  const categories = ['todos', ...new Set(blogPosts.map(post => post.category))];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'todos' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <section id="blog" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      
      {/* Textura de fondo ultra-sutil */}
      <div 
        className="absolute inset-0 z-0 opacity-[0.02]"
        style={{
          backgroundImage: 'radial-gradient(#059669 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}
      />

      <div className="container mx-auto px-4 md:px-6 lg:px-12 relative z-10">
        
        {/* ========================================== */}
        {/* ENCABEZADO CENTRADO                        */}
        {/* ========================================== */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-4 uppercase tracking-wider">
            <FileText className="w-4 h-4" />
            Centro de Conocimiento
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.15] mb-6">
            Insights y tendencias <br className="hidden md:block" />
            <span className="text-emerald-600">para tu industria</span>
          </h2>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed">
            Nuestro equipo de expertos comparte análisis técnicos, casos de estudio y mejores prácticas 
            para la transformación digital de tu empresa.
          </p>
        </motion.div>

        {/* ========================================== */}
        {/* BARRA DE BÚSQUEDA Y FILTROS                */}
        {/* ========================================== */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="max-w-4xl mx-auto mb-12 md:mb-16"
        >
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-slate-50 p-2 rounded-xl border border-slate-200">
            {/* Búsqueda */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar artículos..."
                className="w-full pl-10 pr-4 py-2.5 bg-white rounded-lg border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filtros de categoría */}
            <div className="flex flex-wrap gap-2 justify-center w-full md:w-auto">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 capitalize ${
                    selectedCategory === category
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-white hover:text-slate-900 border border-transparent hover:border-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ========================================== */}
        {/* POST DESTACADO (Solo si no hay búsqueda)   */}
        {/* ========================================== */}
        {searchTerm === '' && selectedCategory === 'todos' && featuredPosts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center gap-2 mb-6">
              <Building2 className="w-5 h-5 text-emerald-600" />
              <h3 className="text-lg font-semibold text-slate-900">Recomendado para ti</h3>
            </div>

            <div 
              onClick={() => setSelectedPost(featuredPosts[0])}
              className="group grid lg:grid-cols-2 gap-0 bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden cursor-pointer hover:border-emerald-500/30 hover:shadow-lg transition-all duration-300"
            >
              <div className="relative aspect-video lg:aspect-auto overflow-hidden">
                <img
                  src={featuredPosts[0].image}
                  alt={featuredPosts[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <span className="inline-block w-fit px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-4 border border-emerald-100">
                  {featuredPosts[0].category}
                </span>
                <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight group-hover:text-emerald-700 transition-colors">
                  {featuredPosts[0].title}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {featuredPosts[0].excerpt}
                </p>
                <div className="flex items-center justify-between mt-auto">
                  <div className="flex items-center gap-3 text-sm text-slate-500">
                    <span>{featuredPosts[0].date}</span>
                    <span className="w-1 h-1 rounded-full bg-slate-300" />
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> {featuredPosts[0].readTime}
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 group-hover:gap-2 transition-all">
                    Leer artículo <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ========================================== */}
        {/* GRID DE ARTÍCULOS                          */}
        {/* ========================================== */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {filteredPosts.map((post) => (
              <motion.article
                key={post.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                onClick={() => setSelectedPost(post)}
                className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-emerald-500/30 hover:shadow-lg hover:shadow-emerald-900/5 transition-all duration-300 cursor-pointer flex flex-col h-full"
              >
                {/* Imagen */}
                <div className="relative aspect-[16/9] overflow-hidden bg-slate-100">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-slate-700 border border-slate-200">
                    {post.category}
                  </span>
                </div>

                {/* Contenido */}
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" /> {post.date}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-slate-300" />
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> {post.readTime}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 mb-2 leading-snug group-hover:text-emerald-700 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  
                  <p className="text-sm text-slate-600 leading-relaxed mb-6 line-clamp-3 flex-grow">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-auto">
                    <span className="text-xs font-medium text-slate-500">
                      Por {post.author}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-600 group-hover:gap-2 transition-all">
                      Leer <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>

        {/* Mensaje sin resultados */}
        {filteredPosts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20 bg-slate-50 rounded-2xl border border-slate-200 border-dashed"
          >
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No se encontraron artículos</h3>
            <p className="text-slate-600 text-sm">Intenta ajustar los términos de búsqueda o selecciona otra categoría.</p>
            <button 
              onClick={() => { setSearchTerm(''); setSelectedCategory('todos'); }}
              className="mt-4 text-sm font-semibold text-emerald-600 hover:text-emerald-700 underline underline-offset-4"
            >
              Limpiar filtros
            </button>
          </motion.div>
        )}
      </div>

      {/* ========================================== */}
      {/* MODAL DE LECTURA (Estilo Premium)          */}
      {/* ========================================== */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 md:p-8 overflow-y-auto"
            onClick={() => setSelectedPost(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.98 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden my-8"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header del Modal */}
              <div className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-slate-200 p-6 flex justify-between items-start z-10">
                <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100">
                  {selectedPost.category}
                </span>
                <button
                  onClick={() => setSelectedPost(null)}
                  className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Contenido del Artículo */}
              <div className="p-6 md:p-10">
                <h1 className="text-2xl md:text-4xl font-bold text-slate-900 leading-tight mb-6">
                  {selectedPost.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-8 pb-8 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                      {selectedPost.author.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{selectedPost.author}</div>
                      <div className="text-xs text-slate-500">{selectedPost.authorRole}</div>
                    </div>
                  </div>
                  <div className="hidden sm:block w-px h-8 bg-slate-200" />
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {selectedPost.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {selectedPost.readTime}</span>
                  </div>
                </div>

                <img
                  src={selectedPost.image}
                  alt={selectedPost.title}
                  className="w-full h-64 md:h-80 object-cover rounded-xl mb-8"
                />

                {/* Cuerpo del texto con tipografía optimizada para lectura */}
                <div 
                  className="prose prose-slate prose-lg max-w-none 
                             prose-headings:font-bold prose-headings:text-slate-900 prose-headings:tracking-tight
                             prose-p:text-slate-600 prose-p:leading-relaxed
                             prose-li:text-slate-600 prose-li:marker:text-emerald-600"
                  dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                />

                {/* Footer del Modal */}
                <div className="mt-12 pt-8 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <p className="text-sm text-slate-500 text-center sm:text-left">
                    ¿Te gustaría implementar una solución como esta en tu empresa?
                  </p>
                  <button className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-emerald-600 transition-colors">
                    Contactar a un experto
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Blog;